import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal, Table, Nav, Tab, Badge } from 'react-bootstrap';
import axios from 'axios';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { feeSettingsService, FeeSettings, SuperAgentFee, AgentFee, UpdateFeeRequest, AddCustomFeeRequest } from '../../services/feeSettingsService';
import { dashboardService, User } from '../../services/dashboardService';
import { telegramAccountsService } from '../../services/telegramAccountsService';
import { apiKeysService } from '../../services/apiKeysService';
import { webhooksService } from '../../services/webhooksService';
import { API_CONFIG, API_ENDPOINTS, getApiBaseUrl } from '../../config/env';

interface EditableFee {
  userId: number;
  percent: number;
  isEditing: boolean;
  tempValue: string;
  feeType: 'superAgent' | 'agent';
}

type PaymentFlowKey = 'directCrypto' | 'fiatOnramp';

const PAYMENT_FLOW_COPY: Record<
  PaymentFlowKey,
  { title: string; badge: string; description: string; modalLabel: string }
> = {
  directCrypto: {
    title: 'Direct Crypto',
    badge: 'DirectCrypto',
    description:
      'Use this for crypto payments that settle in USDC on Polygon. Fee % here is the company commission (Com) taken by the split contract.',
    modalLabel: 'Direct Crypto',
  },
  fiatOnramp: {
    title: 'Fiat Onramp',
    badge: 'FiatOnramp',
    description: 'Payments completed through embedded fiat providers like Onramper and Paybis.',
    modalLabel: 'Fiat Onramp',
  },
};

const FEE_CUSTOMIZE_INFO_BANNER =
  'Fees are configured per payment flow. Direct crypto fee % is the company split (Com). Merchant Dest wallet is set per user; Admin USDC wallet is the fixed Com address for all merchants.';

const SETTINGS_FETCH_TTL_MS = 30_000;

const Settings: React.FC = () => {
  const [feeSettings, setFeeSettings] = useState<FeeSettings | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editableFees, setEditableFees] = useState<EditableFee[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newFee, setNewFee] = useState<{
    userId: number;
    feeType: 'SuperAgent' | 'Agent';
    percent: number;
  }>({
    userId: 0,
    feeType: 'SuperAgent',
    percent: 0,
  });
  
  // Telegram Accounts state
  const [telegramAccounts, setTelegramAccounts] = useState<string[]>([]);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [newTelegramId, setNewTelegramId] = useState('');
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  
  // API Access state
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [webhookToken, setWebhookToken] = useState<string | null>(null);
  const [webhookTokenLoading, setWebhookTokenLoading] = useState(false);
  const [iframeApiKeyInput, setIframeApiKeyInput] = useState('');
  const [iframeFontColor, setIframeFontColor] = useState('#000000');
  const [iframeBackgroundColor, setIframeBackgroundColor] = useState('#ffffff');
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [selectedWalletUser, setSelectedWalletUser] = useState<User | null>(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletModalError, setWalletModalError] = useState<string | null>(null);
  const [feeDataLastLoadedAt, setFeeDataLastLoadedAt] = useState(0);
  const [usersLastLoadedAt, setUsersLastLoadedAt] = useState(0);
  const [telegramLastLoadedAt, setTelegramLastLoadedAt] = useState(0);
  const [apiKeyLastLoadedAt, setApiKeyLastLoadedAt] = useState(0);
  const [webhookLastLoadedAt, setWebhookLastLoadedAt] = useState(0);
  
  const userRole = getCurrentUserRole();
  const userId = getCurrentUserId();
  const tokenUserId = userId ? parseInt(userId, 10) : NaN;
  const localUserId = parseInt(localStorage.getItem('userId') || '', 10);
  const numericUserId = Number.isNaN(tokenUserId)
    ? (Number.isNaN(localUserId) ? null : localUserId)
    : tokenUserId;
  
  // Check if user should see tabs (Admin, SuperAgent, and Agent)
  const shouldShowTabs = userRole === 'Admin' || userRole === 'SuperAgent' || userRole === 'Agent';
  
  // Tab state
  // Agent users start with wallets, Admin and SuperAgent users start with fee-customise
  const [activeTab, setActiveTab] = useState<string>(userRole === 'Agent' ? 'wallets' : 'fee-customise');

  /** Parse failed wallet/address PUT response (JSON ProblemDetails, ASP.NET validation, or plain text). */
  const parseWalletAddressErrorResponse = async (response: Response): Promise<string> => {
    const statusLabel = [response.status, response.statusText].filter(Boolean).join(' ');
    let text: string;
    try {
      text = await response.text();
    } catch {
      return `Wallet update failed (${statusLabel})`;
    }
    if (!text.trim()) {
      return `Wallet update failed (${statusLabel})`;
    }
    try {
      const data = JSON.parse(text) as Record<string, unknown>;
      if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
      if (typeof data.Message === 'string' && data.Message.trim()) return data.Message.trim();
      if (typeof data.detail === 'string' && data.detail.trim()) return data.detail.trim();
      if (typeof data.title === 'string' && data.title.trim()) return data.title.trim();
      if (data.errors && typeof data.errors === 'object' && data.errors !== null) {
        const errs = data.errors as Record<string, string[] | string | undefined>;
        for (const v of Object.values(errs)) {
          if (Array.isArray(v) && v[0]) return String(v[0]);
          if (typeof v === 'string' && v.trim()) return v.trim();
        }
      }
    } catch {
      /* not JSON */
    }
    const trimmed = text.trim();
    return trimmed.length > 280 ? `${trimmed.slice(0, 280)}…` : trimmed;
  };

  // Helper function to safely extract error message from API response
  const getErrorMessage = (err: unknown, defaultMessage: string): string => {
    if (err instanceof Error && typeof err.message === 'string' && err.message.trim() !== '') {
      return err.message.trim();
    }
    if (axios.isAxiosError(err) && err.response?.data) {
      const data = err.response.data;
      // If data has a message property, use it
      if (typeof data === 'object' && data !== null && 'message' in data && typeof data.message === 'string') {
        return data.message;
      }
      // If data is a string, use it
      if (typeof data === 'string') {
        return data;
      }
      // If data is an object, try to extract meaningful error info
      if (typeof data === 'object' && data !== null) {
        // Try common error property names
        if ('error' in data && typeof data.error === 'string') {
          return data.error;
        }
        // If it has expandedError, try to extract from it
        if ('expandedError' in data && typeof data.expandedError === 'string') {
          return data.expandedError;
        }
        // Last resort: stringify the object (but this should be rare)
        return JSON.stringify(data);
      }
    }
    return defaultMessage;
  };

  // Hardcoded default fees
  const DEFAULT_SUPER_AGENT_FEE = 10;
  const DEFAULT_AGENT_FEE = 4;

  useEffect(() => {
    // Lazy-load data based on active tab to reduce startup request burst/rate-limit pressure.
    if ((userRole === 'Admin' || userRole === 'SuperAgent') && activeTab === 'fee-customise') {
      void fetchData();
    } else if (activeTab === 'wallets') {
      dashboardService.invalidateUsersCache();
      void fetchUsersList(true);
    } else if (activeTab === 'telegram-ids') {
      void fetchTelegramAccounts();
    }
  }, [activeTab, userRole]);

  // Fetch API key (for API Access/Iframe tabs) and webhook (API Access only)
  useEffect(() => {
    if (userRole === 'Admin' || userRole === 'SuperAgent' || userRole === 'Agent') {
      if (activeTab === 'api-access') {
        void fetchApiKey();
        void fetchWebhook();
      } else if (activeTab === 'iframe') {
        void fetchApiKey(true, true);
      }
    }
  }, [activeTab, userRole]);

  // Keep iframe key field in sync when a full (unmasked) key is available
  useEffect(() => {
    if (apiKey && !apiKey.includes('*') && !iframeApiKeyInput.trim()) {
      setIframeApiKeyInput(apiKey);
    }
  }, [apiKey, iframeApiKeyInput]);

  const shouldUseCachedData = (lastLoadedAt: number, force = false): boolean => {
    if (force) return false;
    if (!lastLoadedAt) return false;
    return Date.now() - lastLoadedAt < SETTINGS_FETCH_TTL_MS;
  };

  const fetchData = async (force = false) => {
    if (shouldUseCachedData(feeDataLastLoadedAt, force)) return;
    try {
      setLoading(true);
      setError(null);

      const usersData = await dashboardService.getUsers({ force });
      setUsers(usersData);
      setUsersLastLoadedAt(Date.now());

      try {
        const settings = await feeSettingsService.getFeeSettings();
        setFeeSettings(settings);
        setFeeDataLastLoadedAt(Date.now());
        initializeEditableFees(settings, usersData);
      } catch (feeErr) {
        console.error('Error fetching fee settings:', feeErr);
        setError('Failed to fetch fee settings');
      }
    } catch (err) {
      setError('Failed to fetch account data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersList = async (force = false) => {
    if (shouldUseCachedData(usersLastLoadedAt, force)) return;
    try {
      setError(null);
      const usersData = await dashboardService.getUsers({ force: true });
      setUsers(usersData);
      setUsersLastLoadedAt(Date.now());
    } catch (err) {
      setError('Failed to load settlement wallets');
      console.error('Error fetching users:', err);
    }
  };

  const isValidEthereumAddress = (address: string): boolean => /^0x[a-fA-F0-9]{40}$/.test(address);

  // /Users is already scoped server-side per role (Agent: self, SuperAgent: self + agents, Admin: all)
  const walletRows = users;

  /**
   * Backend wallet endpoint usually allows PUT only for the signed-in user; admins may be allowed to update others.
   * Merchants (SuperAgent) editing linked agents here typically get 403 — gate the UI to avoid a dead-end action.
   */
  const canUpdateWalletForUser = (u: User): boolean => {
    if (numericUserId === null) return false;
    if (u.id === numericUserId) return true;
    return userRole === 'Admin';
  };

  /** Users that may receive a custom fee row from the current role (SuperAgents only manage their own Agents). */
  const getFeeEligibleUsers = (role: 'SuperAgent' | 'Agent'): User[] =>
    users.filter((user) => {
      if (user.role !== role) return false;
      if (userRole === 'SuperAgent' && role === 'Agent') {
        return numericUserId !== null && user.createdById === numericUserId;
      }
      return true;
    });

  const handleOpenWalletModal = (user: User) => {
    if (!canUpdateWalletForUser(user)) return;
    setSelectedWalletUser(user);
    setWalletAddress(user.usdtWallet || '');
    setWalletModalError(null);
    setShowWalletModal(true);
  };

  const handleCloseWalletModal = () => {
    setShowWalletModal(false);
    setSelectedWalletUser(null);
    setWalletAddress('');
    setWalletModalError(null);
  };

  const handleUpdateWallet = async () => {
    if (!selectedWalletUser || !walletAddress.trim()) {
      setWalletModalError('Please enter a wallet address');
      return;
    }
    if (!canUpdateWalletForUser(selectedWalletUser)) {
      setWalletModalError(
        'You cannot update this account’s wallet from Settings. Use User Management as an admin, or only edit your own row.'
      );
      return;
    }
    if (!isValidEthereumAddress(walletAddress.trim())) {
      setWalletModalError('Please enter a valid ERC-20 wallet address');
      return;
    }
    try {
      setWalletLoading(true);
      setWalletModalError(null);
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const trimmed = walletAddress.trim();
      const response = await fetch(`${API_ENDPOINTS.USERS}/${selectedWalletUser.id}/address`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ newWalletAddress: trimmed })
      });
      if (!response.ok) {
        let msg = await parseWalletAddressErrorResponse(response);
        if (response.status === 403) {
          const looksGeneric =
            /^Wallet update failed \(\d{3}/i.test(msg) ||
            /^403\b/i.test(msg) ||
            msg.trim().toLowerCase() === 'forbidden';
          if (looksGeneric) {
            const isSelf =
              selectedWalletUser != null &&
              numericUserId !== null &&
              selectedWalletUser.id === numericUserId;
            if (userRole === 'Admin') {
              msg =
                'The server refused this wallet update (forbidden). Your backend may not allow admins to change this user’s payout address on this route, or the policy needs to allow it.';
            } else if (!isSelf) {
              msg =
                'You can only change your own payout wallet in Settings. Updating another account’s wallet requires an administrator (User Management).';
            } else {
              msg =
                'The server refused to update your wallet (403). Try signing out and back in. If it persists, the API may not allow this action for your role.';
            }
          }
        }
        throw new Error(msg || 'Failed to update wallet address');
      }
      setUsers((prev) =>
        prev.map((user) =>
          user.id === selectedWalletUser.id ? { ...user, usdtWallet: walletAddress.trim() } : user
        )
      );
      setSuccess('Wallet updated successfully');
      handleCloseWalletModal();
    } catch (err) {
      setWalletModalError(getErrorMessage(err, 'Failed to update wallet address'));
    } finally {
      setWalletLoading(false);
    }
  };

  const initializeEditableFees = (settings: FeeSettings, usersList: User[]) => {
    const superAgentFees = settings.superAgentFees ?? [];
    const agentFees = settings.agentFees ?? [];
    let fees: EditableFee[] = [];

    if (userRole === 'Admin') {
      fees = [
        ...superAgentFees.map(fee => ({
          userId: fee.userId,
          percent: fee.percent,
          isEditing: false,
          tempValue: fee.percent.toString(),
          feeType: 'superAgent' as const
        })),
        ...agentFees.map(fee => ({
          userId: fee.userId,
          percent: fee.percent,
          isEditing: false,
          tempValue: fee.percent.toString(),
          feeType: 'agent' as const
        }))
      ];
    } else if (userRole === 'SuperAgent') {
      const myAgentIds = new Set(
        usersList
          .filter(
            (u) =>
              u.role === 'Agent' &&
              numericUserId !== null &&
              u.createdById === numericUserId
          )
          .map((u) => u.id)
      );
      fees = agentFees
        .filter((fee) => myAgentIds.has(fee.userId))
        .map((fee) => ({
          userId: fee.userId,
          percent: fee.percent,
          isEditing: false,
          tempValue: fee.percent.toString(),
          feeType: 'agent' as const,
        }));
    }
    
    setEditableFees(fees);
  };

  const getUserName = (userId: number): string => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : `User ${userId}`;
  };

  const handleEditFee = (userId: number, feeType: 'superAgent' | 'agent') => {
    setEditableFees(prev => 
      prev.map(fee => 
        fee.userId === userId && fee.feeType === feeType
          ? { ...fee, isEditing: true, tempValue: fee.percent.toString() }
          : fee
      )
    );
  };

  const handleCancelEdit = (userId: number, feeType: 'superAgent' | 'agent') => {
    setEditableFees(prev => 
      prev.map(fee => 
        fee.userId === userId && fee.feeType === feeType
          ? { ...fee, isEditing: false, tempValue: fee.percent.toString() }
          : fee
      )
    );
  };

  const handleSaveFee = async (userId: number, feeType: 'superAgent' | 'agent') => {
    const editableFee = editableFees.find(fee => 
      fee.userId === userId && fee.feeType === feeType
    );
    if (!editableFee) return;

    const newPercent = parseFloat(editableFee.tempValue);
    if (isNaN(newPercent) || newPercent < 0 || newPercent > 100) {
      setError('Please enter a valid percentage between 0 and 100');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      
      const updateRequest: UpdateFeeRequest = {
        type: feeType === 'superAgent' ? 'SuperAgent' : 'Agent',
        targetId: userId,
        percent: newPercent
      };

      const response = await feeSettingsService.updateFeeSettings(updateRequest);
      
      if (response.updated) {
        // Update local state
        setFeeSettings(prev => {
          if (!prev) return prev;
          
          if (feeType === 'superAgent') {
            return {
              ...prev,
              superAgentFees: prev.superAgentFees.map(fee => 
                fee.userId === userId ? { ...fee, percent: newPercent } : fee
              )
            };
          } else {
            return {
              ...prev,
              agentFees: prev.agentFees.map(fee => 
                fee.userId === userId ? { ...fee, percent: newPercent } : fee
              )
            };
          }
        });

        setEditableFees(prev => 
          prev.map(fee => 
            fee.userId === userId && fee.feeType === feeType
              ? { ...fee, isEditing: false, percent: newPercent, tempValue: newPercent.toString() }
              : fee
          )
        );

        setSuccess('Fee updated successfully');
      } else {
        setError('Failed to update fee - server returned false');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update fee'));
      console.error('Error updating fee:', err);
    }
  };

  const handleAddCustomFee = async () => {
    const availableUsers = getFeeEligibleUsers(newFee.feeType);
    
    if (availableUsers.length === 0) {
      setError(
        userRole === 'SuperAgent' && newFee.feeType === 'Agent'
          ? 'No Agents linked to your account are available for custom fees.'
          : `No ${newFee.feeType} users available to assign custom fees.`
      );
      return;
    }
    
    if (
      newFee.userId === 0 ||
      !Number.isFinite(newFee.percent) ||
      newFee.percent <= 0 ||
      newFee.percent > 100
    ) {
      setError('Please select a user and enter a valid percentage (greater than 0, up to 100).');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      
      const addRequest: AddCustomFeeRequest = {
        userId: newFee.userId,
        feeType: newFee.feeType,
        percent: newFee.percent
      };

      const response = await feeSettingsService.addCustomFee(addRequest);
      
      if (response.updated) {
        await fetchData();
        setShowAddModal(false);
        setNewFee({
          userId: 0,
          feeType: newFee.feeType,
          percent: 0,
        });
        setSuccess('Custom fee added successfully');
      } else {
        setError('Failed to add custom fee - server returned false');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to add custom fee'));
      console.error('Error adding custom fee:', err);
    }
  };

  const handleTempValueChange = (userId: number, feeType: 'superAgent' | 'agent', value: string) => {
    setEditableFees(prev => 
      prev.map(fee => 
        fee.userId === userId && fee.feeType === feeType
          ? { ...fee, tempValue: value }
          : fee
      )
    );
  };

  const handleOpenAddModal = (feeType: 'SuperAgent' | 'Agent') => {
    setNewFee({
      userId: 0,
      feeType,
      percent: 0,
    });
    setShowAddModal(true);
  };

  // Telegram Accounts functions
  const fetchTelegramAccounts = async (force = false) => {
    if (shouldUseCachedData(telegramLastLoadedAt, force)) return;
    try {
      setTelegramLoading(true);
      const accounts = await telegramAccountsService.getTelegramAccounts();
      setTelegramAccounts(accounts);
      setTelegramLastLoadedAt(Date.now());
    } catch (err) {
      console.error('Error fetching telegram accounts:', err);
      if (force) {
        setError(getErrorMessage(err, 'Failed to load Telegram accounts'));
      }
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleAddTelegramAccount = async () => {
    if (!newTelegramId.trim()) {
      setError('Please enter a Telegram ID');
      return;
    }

    // Basic validation for Telegram ID (should be numeric)
    if (!/^\d+$/.test(newTelegramId.trim())) {
      setError('Telegram ID must be numeric');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      
      const addedId = newTelegramId.trim();
      await telegramAccountsService.addTelegramAccount(addedId);

      setTelegramAccounts((prev) =>
        prev.includes(addedId) ? prev : [...prev, addedId]
      );
      setTelegramLastLoadedAt(0);

      setSuccess('Telegram account added successfully');
      setNewTelegramId('');
      setShowTelegramModal(false);
      await fetchTelegramAccounts(true);
    } catch (err) {
      // Error means non-200 status code was returned
      const errorMessage = getErrorMessage(err, 'Failed to add Telegram account');
      setError(errorMessage);
      console.error('Error adding telegram account:', err);
    }
  };

  const handleDeleteTelegramAccount = async (telegramId: string) => {
    if (!window.confirm('Are you sure you want to delete this Telegram account?')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      
      await telegramAccountsService.deleteTelegramAccount(telegramId);

      setTelegramAccounts((prev) => prev.filter((id) => id !== telegramId));
      setTelegramLastLoadedAt(0);

      setSuccess('Telegram account deleted successfully');
      await fetchTelegramAccounts(true);
    } catch (err) {
      // Error means non-200 status code was returned
      const errorMessage = getErrorMessage(err, 'Failed to delete Telegram account');
      setError(errorMessage);
      console.error('Error deleting telegram account:', err);
    }
  };

  const handleOpenTgUserId = () => {
    window.open('https://tg-user.id/', '_blank');
  };

  const copyToClipboard = async (value: string, successMessage?: string) => {
    try {
      await navigator.clipboard.writeText(value);
      if (successMessage) {
        setSuccess(successMessage);
      }
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      setError('Failed to copy to clipboard');
    }
  };

  // API Keys functions
  const fetchApiKey = async (skipLoading = false, force = false) => {
    if (shouldUseCachedData(apiKeyLastLoadedAt, force)) return;
    try {
      if (!skipLoading) {
        setApiKeyLoading(true);
      }
      setError(null);
      const key = await apiKeysService.getApiKey();
      if (key && key.trim().length > 0) {
        setApiKey(key);
        // Full keys (just generated) can pre-fill iframe; masked keys cannot.
        if (!key.includes('*')) {
          setIframeApiKeyInput(key);
        }
      } else {
        setApiKey(null);
      }
      setApiKeyLastLoadedAt(Date.now());
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          // No API key exists yet
          setApiKey(null);
        } else {
          // Other errors - log but don't set error state to avoid blocking UI
          // But still set apiKey to null to show "No API key generated yet"
          console.error('Error fetching API key:', err);
          console.error('Response status:', err.response?.status);
          console.error('Response data:', err.response?.data);
          setApiKey(null);
        }
      } else {
        console.error('Error fetching API key:', err);
        setApiKey(null);
      }
    } finally {
      if (!skipLoading) {
        setApiKeyLoading(false);
      }
    }
  };

  const handleGenerateApiKey = async () => {
    if (!window.confirm('Are you sure you want to generate a new API key? This will replace your existing key.')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setApiKeyLoading(true);
      
      // Generate the API key - POST returns {"token":"..."} with full uncensored key
      const newKey = await apiKeysService.generateApiKey();
      if (!newKey.trim()) {
        throw new Error('API key was not returned by the server');
      }
      setApiKey(newKey);
      setIframeApiKeyInput(newKey);
      setApiKeyLastLoadedAt(Date.now());
      setSuccess('API key generated successfully — copy it now; only a masked version is shown after you leave this page.');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to generate API key');
      setError(errorMessage);
      console.error('Error generating API key:', err);
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleDeleteApiKey = async () => {
    if (!window.confirm('Are you sure you want to delete your API key? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setApiKeyLoading(true);
      
      await apiKeysService.deleteApiKey();
      setApiKey(null);
      setIframeApiKeyInput('');
      setSuccess('API key deleted successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to delete API key');
      setError(errorMessage);
      console.error('Error deleting API key:', err);
    } finally {
      setApiKeyLoading(false);
    }
  };

  // Webhooks functions
  const fetchWebhook = async (force = false) => {
    if (shouldUseCachedData(webhookLastLoadedAt, force)) return;
    try {
      setWebhookLoading(true);
      const url = await webhooksService.getWebhook();
      setWebhookUrl(url);
      setWebhookLastLoadedAt(Date.now());
    } catch (err) {
      console.error('Error fetching webhook:', err);
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleSetWebhook = async () => {
    if (!newWebhookUrl.trim()) {
      setError('Please enter a webhook URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(newWebhookUrl.trim());
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setWebhookLoading(true);
      
      await webhooksService.setWebhook(newWebhookUrl.trim());
      setWebhookUrl(newWebhookUrl.trim());
      setNewWebhookUrl('');
      setSuccess('Webhook URL set successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to set webhook URL');
      setError(errorMessage);
      console.error('Error setting webhook:', err);
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleDeleteWebhook = async () => {
    if (!window.confirm('Are you sure you want to delete your webhook URL?')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setWebhookLoading(true);
      
      await webhooksService.deleteWebhook();
      setWebhookUrl(null);
      setSuccess('Webhook URL deleted successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to delete webhook URL');
      setError(errorMessage);
      console.error('Error deleting webhook:', err);
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleGenerateWebhookToken = async () => {
    try {
      setError(null);
      setSuccess(null);
      setWebhookTokenLoading(true);
      
      const token = await webhooksService.generateWebhookToken();
      setWebhookToken(token);
      setSuccess('Webhook token generated successfully');
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to generate webhook token');
      setError(errorMessage);
      console.error('Error generating webhook token:', err);
    } finally {
      setWebhookTokenLoading(false);
    }
  };

  // Render Fee Customise tab content (for Admin and SuperAgent)
  const renderFeeCustomiseTab = () => {
    if (!feeSettings) {
      return (
        <Card className="rounded-2xl p-4 border border-slate-200 bg-white shadow-sm">
          <Card.Body className="text-center">
            <Spinner animation="border" role="status" className="text-[#2297d1]">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </Card.Body>
        </Card>
      );
    }

    const renderFlowCard = (flow: PaymentFlowKey, inner: React.ReactNode) => (
      <Card
        key={flow}
        className="mb-4 rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden"
      >
        <Card.Body className="!p-0">

          <div className="p-[30px] border-b-[1px]">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 ">
            <h4 className="text-slate-900 mb-0 fw-semibold fs-5">
              {PAYMENT_FLOW_COPY[flow].title}
            </h4>
             <p className='font-semibold text-[#4A4A4A]'> {PAYMENT_FLOW_COPY[flow].badge}</p>
            {/* </Badge> */}
          </div>
          <p className="text-slate-600 mt-3 small lh-base">
            {PAYMENT_FLOW_COPY[flow].description}
          </p>
          </div>
<div className='p-[30px]'>

          {inner}
</div>
        </Card.Body>
      </Card>
    );

    const adminFlowBody = (flow: PaymentFlowKey) => (
      <Row className="g-4">
        <Col md={6}>
          {renderDefaultFeeCard('Default SuperAgent Pay Fee', DEFAULT_SUPER_AGENT_FEE)}
          {renderCustomFeesTable(
            editableFees.filter((fee) => fee.feeType === 'superAgent'),
            'superAgent',
            flow === 'directCrypto'
              ? 'Custom SuperAgent Pay Fee for Direct Crypto'
              : 'Custom SuperAgent Pay Fee for Fiat Onramp',
            { flowEmptyKey: flow }
          )}
        </Col>
        <Col md={6}>
          {renderDefaultFeeCard('Default Agent Pay Fee', DEFAULT_AGENT_FEE)}
          {renderCustomFeesTable(
            editableFees.filter((fee) => fee.feeType === 'agent'),
            'agent',
            flow === 'directCrypto'
              ? 'Custom Agent Pay Fee for Direct Crypto'
              : 'Custom Agent Pay Fee for Fiat Onramp',
            { flowEmptyKey: flow }
          )}
        </Col>
      </Row>
    );

    const superAgentFlowBody = (flow: PaymentFlowKey) => (
      <>
        {renderDefaultFeeCard('Default Agent Pay Fee', DEFAULT_AGENT_FEE)}
        {renderCustomFeesTable(
          editableFees.filter((fee) => fee.feeType === 'agent'),
          'agent',
          flow === 'directCrypto'
            ? 'Custom Agent Pay Fee for Direct Crypto'
            : 'Custom Agent Pay Fee for Fiat Onramp',
          { flowEmptyKey: flow }
        )}
      </>
    );

    return (
      <>
        <Alert
          variant="info"
          className="mb-4 rounded-xl border border-[#b8d9ec] bg-[#2297D121] text-slate-800 shadow-sm p-4"
        >
          <p className="mb-0 small lh-base">{FEE_CUSTOMIZE_INFO_BANNER}</p>
        </Alert>
        {userRole === 'Admin' &&
          (['directCrypto', 'fiatOnramp'] as const).map((flow) =>
            renderFlowCard(flow, adminFlowBody(flow))
          )}
        {userRole === 'SuperAgent' &&
          (['directCrypto', 'fiatOnramp'] as const).map((flow) =>
            renderFlowCard(flow, superAgentFlowBody(flow))
          )}
      </>
    );
  };

  // Render Telegram IDs tab content
  const renderTelegramIdsTab = () => (
    <Row>
      <Col>
        <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <Card.Header className="!p-[30px] d-flex justify-content-between align-items-center">
            <h5 className="m-0 text-slate-900">Telegram Accounts</h5>
            <div>
              <Button 
                variant="outline-info" 
                size="sm"
                onClick={handleOpenTgUserId}
                className="me-2 py-2 text-sm"
              >
                Get Telegram ID from username
              </Button>
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setShowTelegramModal(true)}
              >
                Add Account
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {telegramLoading ? (
              <div className="d-flex justify-content-center p-[30px]">
                <Spinner animation="border" role="status" className="text-[#2297d1]">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : telegramAccounts.length === 0 ? (
              <p className="m-0 text-center text-slate-500">
                No Telegram accounts added yet
              </p>
            ) : (
              <Table responsive className="border-slate-200 align-middle text-slate-900">
                <thead>
                  <tr>
                    <th>Telegram ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {telegramAccounts.map((telegramId) => (
                    <tr key={telegramId}>
                      <td>
                        <span className="fw-bold text-[#2297d1]">
                          {telegramId}
                        </span>
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteTelegramAccount(telegramId)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  // Render API Access tab content
  const renderWalletsTab = () => (
    <Row>
      <Col>
        <Alert variant="info" className="mb-4">
          <p className="mb-2">
            Update your <strong>own</strong> settlement wallet below. Changes apply to future payment requests and
            invoices only.
          </p>
          {userRole === 'Admin' ? (
            <p className="mb-0 small">
              As an admin you can also set wallets for other accounts in this table. If an update is rejected by the
              server, the API policy for <code className="small">PUT …/Users/:id/address</code> may need to allow your
              role.
            </p>
          ) : (
            <p className="mb-0 small">
              To change a <strong>linked</strong> account’s wallet, an admin must do it in{' '}
              <Link to="/users">User Management</Link> (or the API must allow your role to update that user).
            </p>
          )}
        </Alert>
        <Card className='!rounded-xl'>
          <Card.Header className="d-flex justify-content-between align-items-center !p-0">
            <div className='p-[30px] flex justify-between items-center w-full'>
              <h5 className="m-0 text-slate-900">Settlement Wallets</h5>
            <Button
              variant="outline-secondary"
              size="sm"
              // className="!bg-[#2297D1] !text-[#2297d1] hover:!bg-[#2297d1] hover:!text-white"s
              onClick={() => void fetchUsersList(true)}
            >
              Refresh
            </Button>
            </div>
          </Card.Header>
          <Card.Body className="py-2">
            <Table responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>USER</th>
                  <th>ROLE</th>
                  <th>WALLET</th>
                  <th>SCOPE</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {walletRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">
                      No wallets found
                    </td>
                  </tr>
                ) : (
                  walletRows.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="fw-semibold">{user.name}</div>
                        <div className="text-muted small">{user.email}</div>
                      </td>
                      <td>{user.role === 'SuperAgent' ? 'Merchant' : user.role}</td>
                      <td>
                        <span className="font-semibold text-[#2297d1]">
                          {user.usdtWallet || 'No wallet address'}
                        </span>
                      </td>
                      <td>{user.id === numericUserId ? 'Your account' : 'Linked account'}</td>
                      <td>
                        {canUpdateWalletForUser(user) ? (
                          <Button variant="outline-primary" size="sm" onClick={() => handleOpenWalletModal(user)}>
                            Update Wallet
                          </Button>
                        ) : (
                          <span
                            className="text-muted small"
                            title="Only your own wallet can be updated here. Ask an admin to use User Management for other accounts."
                          >
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  // Render API Access tab content
  const renderApiAccessTab = () => {
    const paymentLinkEndpoint = `${getApiBaseUrl()}/payment-links/generate`;

    return (
      <Row>
        <Col>
          <Card className="mb-4 rounded-2xl border border-slate-200 bg-white shadow-sm">
            <Card.Header className='p-[30px]'>
              <h5 className="m-0 text-slate-900 font-semibold">API Key</h5>
            </Card.Header>
            <Card.Body>
              {apiKeyLoading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" className="text-[#2297d1]">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : apiKey ? (
                <div className="mb-3">
                  <Form.Label>Your API Key</Form.Label>
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <Form.Control
                      type="text"
                      value={apiKey}
                      readOnly
                      className="me-2 font-mono flex-grow-1"
                    />
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => copyToClipboard(apiKey, 'API key copied to clipboard')}
                      disabled={apiKey.includes('*')}
                    >
                      Copy
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleDeleteApiKey}
                    >
                      Delete
                    </Button>
                  </div>
                  {apiKey.includes('*') && (
                    <p className="mt-2 mb-0 text-slate-500 small">
                      Masked preview only. Regenerate to obtain a new full key (required for iframe embeds).
                    </p>
                  )}
                </div>
              ) : (
                <p className="mb-3 text-slate-500">
                  No API key generated yet
                </p>
              )}
              <Button
                variant="primary"
                onClick={handleGenerateApiKey}
                disabled={apiKeyLoading}
              >
                {apiKey ? 'Regenerate API Key' : 'Generate API Key'}
              </Button>
            </Card.Body>
          </Card>

          <Card className="mb-4 rounded-2xl border border-slate-200 bg-white shadow-sm">
            <Card.Header className='font-semibold p-[30px]'>
              <h5 className="m-0 text-slate-900">Payment Link Endpoint</h5>
            </Card.Header>
            <Card.Body className='!p-[30px]'>
              <div className="mb-3">
                <Form.Label>Endpoint URL</Form.Label>
                <Form.Control
                  type="text"
                  value={paymentLinkEndpoint}
                  readOnly
                  className="font-mono text-[#2297d1]"
                />
              </div>
              <div className="mb-3">
                <Form.Label>Required Headers</Form.Label>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="font-mono text-slate-700">
                    Content-Type: application/json
                  </div>
                  <div className="font-mono text-slate-700">
                    X-COR-ApiKey: &lt;your API key&gt;
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <Form.Label>Available User IDs</Form.Label>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="mb-2 text-slate-700">
                    <strong>Your userId:</strong> {userId || 'Unavailable'}
                  </p>
                  {userRole === 'SuperAgent' && numericUserId !== null ? (
                    <>
                      {users.some(u => u.role === 'Agent' && u.createdById === numericUserId) ? (
                        <>
                          <p className="mb-2 text-slate-500">
                            Agents you can reference:
                          </p>
                          <ul className="mb-0 pl-5 text-slate-700">
                            {users
                              .filter(u => u.role === 'Agent' && u.createdById === numericUserId)
                              .map(agent => (
                                <li key={agent.id}>
                                  {agent.name || 'Agent'} — userId: {agent.id}
                                </li>
                              ))}
                          </ul>
                        </>
                      ) : (
                        <p className="mb-0 text-slate-500">
                          You currently have no linked Agents. Use your own userId.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="mb-0 text-slate-500">
                      Use your own userId when generating payment links.
                    </p>
                  )}
                </div>
              </div>
              <div className="mb-3">
                <Form.Label>Method</Form.Label>
                <Form.Control
                  type="text"
                  value="POST"
                  readOnly
                  className="w-24 font-mono"
                />
              </div>
              <div className="mb-3">
                <Form.Label>Request Body</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={JSON.stringify({
                    userId: 0,
                    fiatCurrency: "string",
                    amount: 0
                  }, null, 2)}
                  readOnly
                  className="font-mono text-emerald-600"
                />
              </div>
              <div className="mb-3">
                <Form.Label>Response Body</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={JSON.stringify({
                    transactionId: 0,
                    paymentLinks: ["string"],
                    uniqueWalletAddress: "string"
                  }, null, 2)}
                  readOnly
                  className="font-mono text-emerald-600"
                />
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-4 rounded-2xl border border-slate-200 bg-white shadow-sm">
            <Card.Header className='font-semibold !p-[30px]'>
              <h5 className="m-0 text-slate-900">Webhook</h5>
            </Card.Header>
            <Card.Body className='!p-[30px]'>
              {webhookLoading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status" className="text-[#2297d1]">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <>
                  {webhookUrl ? (
                    <div className="mb-3">
                      <Form.Label>Current Webhook URL</Form.Label>
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="text"
                          value={webhookUrl}
                          readOnly
                          className="me-2 font-mono"
                        />
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleDeleteWebhook}
                        >
                          Delete
                        </Button>
                      </div>
                      <Form.Text className="text-slate-500">
                        Delete the current webhook to add a new one.
                      </Form.Text>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <Form.Label>Webhook Token</Form.Label>
                        {webhookToken ? (
                          <div className="d-flex align-items-center mb-2">
                            <Form.Control
                              type="text"
                              value={webhookToken}
                              readOnly
                              className="me-2 font-mono text-emerald-600"
                            />
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="!border-[#2297d1] !text-[#2297d1] hover:!bg-[#2297d1] hover:!text-white"
                              onClick={() => {
                                navigator.clipboard.writeText(webhookToken);
                                setSuccess('Token copied to clipboard');
                              }}
                            >
                              Copy
                            </Button>
                          </div>
                        ) : (
                          <p className="mb-3 text-slate-500">
                            No webhook token generated yet. Generate a token first to set a webhook URL.
                          </p>
                        )}
                        <Button
                          variant="outline-primary"
                          onClick={handleGenerateWebhookToken}
                          disabled={webhookTokenLoading}
                        >
                          {webhookTokenLoading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Generating...
                            </>
                          ) : (
                            'Generate Webhook Token'
                          )}
                        </Button>
                      </div>

                      <div className="mb-3">
                        <Form.Label>Set Webhook URL</Form.Label>
                        {!webhookToken && (
                          <p className="mb-2 text-sm text-amber-600">
                            ⚠️ Generate a webhook token first before setting a webhook URL.
                          </p>
                        )}
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            value={newWebhookUrl}
                            onChange={(e) => setNewWebhookUrl(e.target.value)}
                            placeholder="https://your-domain.com/webhook"
                            disabled={!webhookToken}
                            className="me-2 disabled:cursor-not-allowed disabled:bg-slate-100"
                          />
                          <Button
                            variant="primary"
                            onClick={handleSetWebhook}
                            disabled={webhookLoading || !webhookToken}
                          >
                            Set
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  <Card className="mt-3 rounded-xl border border-slate-200 bg-slate-50">
                    <Card.Header>
                      <h6 className="m-0 text-slate-900">Webhook Documentation</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <h6 className="text-slate-900">Webhook Headers</h6>
                        <p className="text-sm text-slate-500">
                          Signature is a HMAC SHA256 signature of the body.
                        </p>
                        <p className="text-sm text-slate-500">
                          Signature secret is a secret token generated by /api/webhooks/generate-token endpoint.
                        </p>
                        <code className="block rounded bg-white p-2 text-emerald-600">
                          X-COR-Signature: xxxxxx
                        </code>
                      </div>
                      
                      <div className="mb-3">
                        <h6 className="text-slate-900">Webhook Payload</h6>
                        <p className="mb-2 text-sm text-slate-500">
                          <strong>On Endpoint Configuration:</strong>
                        </p>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={JSON.stringify({
                            action: "Authorization",
                            payload: null
                          }, null, 2)}
                          readOnly
                          className="mb-3 font-mono text-sm text-emerald-600"
                        />
                        
                        <p className="mb-2 text-sm text-slate-500">
                          <strong>On Transaction:</strong>
                        </p>
                        <Form.Control
                          as="textarea"
                          rows={10}
                          value={JSON.stringify({
                            action: "TransactionCompleted",
                            payload: {
                              TransactionId: "...",
                              FiatCurrency: "...",
                              FiatAmount: 0,
                              CryptoCurrency: "...",
                              CryptoAmount: 0,
                              UserWallet: "...",
                              TxHash: "...",
                              Status: "..."
                            }
                          }, null, 2)}
                          readOnly
                          className="font-mono text-sm text-emerald-600"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderIframeTab = () => {
    const hasIframeKey = iframeApiKeyInput.trim().length > 0;
    const fontParam = encodeURIComponent(iframeFontColor);
    const backgroundParam = encodeURIComponent(iframeBackgroundColor);
    const userIdParam = userId ? `&userid=${encodeURIComponent(userId)}` : '';
    const iframeUrl = hasIframeKey
      ? `${window.location.origin}/iframe/${iframeApiKeyInput.trim()}?fontcolor=${fontParam}&backgroundcolor=${backgroundParam}${userIdParam}`
      : '';
    const iframeSnippet = hasIframeKey
      ? `<iframe src="${iframeUrl}" style="width:100%;height:600px;border:0;" title="ZK Gate Payment Links"></iframe>`
      : '';

    return (
      <Row>
        <Col>
          <Card className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <Card.Header className='font-semibold !p-[30px]'>
              <h5 className="m-0 text-slate-900">Iframe Configuration</h5>
            </Card.Header>
            <Card.Body className='!p-[30px]'>
              <Alert variant="info" className="border-sky-200 bg-sky-50 text-sky-900">
                Paste an API key generated from the API Access tab (or generate a new one) and customize the colors that will be passed as query parameters (<code>fontcolor</code>, <code>backgroundcolor</code>).
              </Alert>

              <Form.Group className="mb-4">
                <Form.Label className="font-semibold">Iframe API Key</Form.Label>
                <Form.Control
                  type="text"
                  value={iframeApiKeyInput}
                  onChange={(e) => setIframeApiKeyInput(e.target.value)}
                  placeholder="Paste API key here"
                />
                <Form.Text className="text-slate-500">
                  This key authenticates requests sent from the iframe page. Paste a full key from API Access, or generate a new one below (masked keys cannot be used).
                </Form.Text>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="!border-[#2297d1] !text-[#2297d1] hover:!bg-[#2297d1] hover:!text-white"
                    onClick={handleGenerateApiKey}
                    disabled={apiKeyLoading}
                  >
                    {apiKeyLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generating...
                      </>
                    ) : (
                      'Generate new API key'
                    )}
                  </Button>
                </div>
              </Form.Group>

              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="font-semibold">Font Color</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <Form.Control
                        type="color"
                        value={iframeFontColor}
                        onChange={(e) => setIframeFontColor(e.target.value)}
                        title="Font color"
                        className="h-[60px] w-[60px] border-0 bg-transparent p-0"
                      />
                      <Form.Control
                        type="text"
                        value={iframeFontColor}
                        onChange={(e) => setIframeFontColor(e.target.value)}
                      />
                    </div>
                    <Form.Text className="text-slate-500">
                      Controls the <code>fontcolor</code> query parameter.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="font-semibold">Background Color</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <Form.Control
                        type="color"
                        value={iframeBackgroundColor}
                        onChange={(e) => setIframeBackgroundColor(e.target.value)}
                        title="Background color"
                        className="h-[60px] w-[60px] border-0 bg-transparent p-0"
                      />
                      <Form.Control
                        type="text"
                        value={iframeBackgroundColor}
                        onChange={(e) => setIframeBackgroundColor(e.target.value)}
                      />
                    </div>
                    <Form.Text className="text-slate-500">
                      Controls the <code>backgroundcolor</code> query parameter.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-4">
                <Form.Label className="font-semibold">Iframe URL</Form.Label>
                <Form.Control
                  type="text"
                  value={iframeUrl}
                  placeholder="Enter an API key to generate the iframe URL"
                  readOnly
                  className="font-mono text-[#2297d1]"
                />
                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => copyToClipboard(iframeUrl, 'Iframe URL copied to clipboard')}
                    disabled={!hasIframeKey}
                  >
                    Copy URL
                  </Button>
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => window.open(iframeUrl, '_blank')}
                    disabled={!hasIframeKey}
                  >
                    Open Preview
                  </Button>
                </div>
              </Form.Group>

              <Form.Group>
                <Form.Label className="font-semibold">Embed Snippet</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={iframeSnippet}
                  placeholder="Enter an API key to generate the embed snippet"
                  readOnly
                  className="font-mono"
                />
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-3"
                  onClick={() => copyToClipboard(iframeSnippet, 'Iframe snippet copied to clipboard')}
                  disabled={!hasIframeKey}
                >
                  Copy Embed Code
                </Button>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderDefaultFeeCard = (title: string, fee: number) => (
    <Card className="mb-3 rounded-3xl border border-slate-200 bg-white shadow-sm">
      <Card.Body className="py-3 px-4">
        <Card.Title className="text-slate-900 fs-6 fw-semibold mb-2">{title}</Card.Title>
        <div className="d-flex align-items-baseline flex-wrap gap-2">
          <span className="fs-3 fw-bold text-[#2297d1]">{fee}%</span>
          <span className="text-slate-500 small">(Default)</span>
        </div>
      </Card.Body>
    </Card>
  );

  const renderCustomFeesTable = (
    fees: EditableFee[],
    feeType: 'superAgent' | 'agent',
    title: string,
    options?: { flowEmptyKey?: PaymentFlowKey }
  ) => {
    const eligible = getFeeEligibleUsers(feeType === 'superAgent' ? 'SuperAgent' : 'Agent');
    const flowEmptyMessage =
      options?.flowEmptyKey &&
      (feeType === 'agent'
        ? options.flowEmptyKey === 'directCrypto'
          ? 'No custom fees set for direct crypto.'
          : 'No custom fees set for fiat onramp.'
        : options.flowEmptyKey === 'directCrypto'
          ? 'No custom SuperAgent fees set for direct crypto.'
          : 'No custom SuperAgent fees set for fiat onramp.');

    const bodyIsTable = eligible.length > 0 && fees.length > 0;

    return (
    <Card className="mb-0 rounded-3xl border border-slate-200 bg-white shadow-sm">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-3 px-4 bg-white border-bottom border-slate-200">
        <span className="m-0 fw-semibold text-slate-900">{title}</span>
        <Button 
          variant="primary" 
          size="sm"
          className="rounded-pill px-3"
          disabled={eligible.length === 0}
          onClick={() =>
            handleOpenAddModal(feeType === 'superAgent' ? 'SuperAgent' : 'Agent')
          }
        >
          Add Custom Fee
        </Button>
      </Card.Header>
      <Card.Body
        className={
          bodyIsTable
            ? 'py-3 px-4'
            : 'py-4 px-4 min-h-[4.5rem] d-flex align-items-center justify-content-center'
        }
      >
        {eligible.length === 0 ? (
          <p className="m-0 small text-center text-slate-500 mb-0 w-100">
            {userRole === 'SuperAgent' && feeType === 'agent'
              ? 'No Agents are linked to your merchant account for custom fees.'
              : `No ${feeType === 'superAgent' ? 'SuperAgent' : 'Agent'} users available yet.`}
          </p>
        ) : fees.length === 0 ? (
          <p className="m-0 small text-center text-slate-500 mb-0 w-100">
            {flowEmptyMessage ?? `No custom ${feeType === 'superAgent' ? 'SuperAgent' : 'Agent'} fees yet.`}
          </p>
        ) : (
          <div className="w-100 py-1">
            <Table responsive className="text-slate-900 mb-0 align-middle">
            <thead>
              <tr>
                <th>User</th>
                <th>Fee %</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fees.map(fee => (
                <tr key={`${fee.userId}-${fee.feeType}`}>
                  <td>{getUserName(fee.userId)}</td>
                  <td>
                    {fee.isEditing ? (
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="number"
                          value={fee.tempValue}
                          onChange={(e) => handleTempValueChange(fee.userId, feeType, e.target.value)}
                          min="0"
                          max="100"
                          step="0.01"
                          size="sm"
                          className="me-2 w-[100px]"
                        />
                        <span className="me-2">%</span>
                      </div>
                    ) : (
                      <span className="fw-bold text-[#2297d1]">{fee.percent}%</span>
                    )}
                  </td>
                  <td>
                    {fee.isEditing ? (
                      <div>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleSaveFee(fee.userId, feeType)}
                          className="me-2"
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="!border-[#2297d1] !bg-[#2297d1] !text-white hover:!border-[#1d81b2] hover:!bg-[#1d81b2]"
                          onClick={() => handleCancelEdit(fee.userId, feeType)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditFee(fee.userId, feeType)}
                      >
                        Edit
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          </div>
        )}
      </Card.Body>
    </Card>
    );
  };

  if (loading && (userRole === 'Admin' || userRole === 'SuperAgent')) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e0ed] via-[#e4eaf1] to-[#eef2f7]">
        <Container className="pt-4">
          <div className="d-flex justify-content-center">
            <Spinner animation="border" role="status" className="text-[#2297d1]">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Container>
      </div>
    );
  }

  if (error && !feeSettings && (userRole === 'Admin' || userRole === 'SuperAgent')) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e0ed] via-[#e4eaf1] to-[#eef2f7]">
        <Container className="pt-4">
          <Alert variant="danger">
            {error}
            <Button variant="outline-light" className="ms-2" onClick={() => void fetchData(true)}>
              Retry
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="settings-page">
    
      <Container className="py-4">
        <Row>
          <Col>
            <div className="settings-shell bg-[#f6f6f6] rounded-[30px] blurLayerInteractiveInteractive p-[30px] mb-[40px]">
              <div className="flex justify-content-between align-items-start flex-wrap gap-5">
                <div className='space-y-2'>
                  <p className="mb-1 text-base uppercase text-[#4A4A4A]">
                    Workspace Controls
                  </p>
                  <h2 className="mb-2 lg:text-[40px] text-[24px] font-semibold">Settings</h2>
                  <p className="mb-0 settings-muted">
                    Manage fees, payout wallets, Telegram accounts, API access, webhooks, and iframe setup.
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className='border-[1px] border-[#d2d8dd] px-[13px] py-2 rounded-[6px] text-sm font-semibold text-slate-700 hover:bg-slate-50'
                    onClick={() => {
                      if (activeTab === 'wallets') {
                        void fetchUsersList(true);
                        return;
                      }
                      if (activeTab === 'fee-customise') {
                        void fetchData(true);
                        return;
                      }
                      if (activeTab === 'telegram-ids') {
                        void fetchTelegramAccounts(true);
                        return;
                      }
                      if (activeTab === 'api-access') {
                        void Promise.all([fetchApiKey(false, true), fetchWebhook(true)]);
                        return;
                      }
                      if (activeTab === 'iframe') {
                        void fetchApiKey(true, true);
                      }
                    }}
                  >
                    Refresh
                  </button>
                  <button className='bg-[#2297d1] text-white px-[13px] py-2 rounded-[6px] text-sm font-semibold hover:bg-[#1b7fb3]' onClick={() => copyToClipboard(JSON.stringify(walletRows, null, 2), 'Settings data copied')}>
                    Export CSV
                  </button>
                </div>
              </div>
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <div className="rounded-lg  bg-[#F0F0F0] p-3">
                    <div className="small text-muted mb-1">Role</div>
                    <div className="fw-semibold">{userRole === 'SuperAgent' ? 'Merchant' : userRole}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="rounded-lg  bg-[#F0F0F0] p-3">
                    <div className="small text-muted mb-1">Tabs Available</div>
                    <div className="fw-semibold">Role-based settings enabled</div>
                  </div>
                </Col>
              </Row>
            </div>

            <Row className="g-3 mb-[40px]">
              <Col md={6} lg={3}>
                <div className="rounded-[20px] overflow-hidden h-full">
                  <div className="border-l bg-white py-2 px-[22px] h-full shadow-md" style={{ borderLeft: '6px solid #2297D1' }}>
                    <p className="text-[11px] xl:text-base uppercase text-[#2297D1]">Visible Rows</p>
                    <p className="mt-2 text-2xl font-bold tracking-tight text-black sm:text-3xl">
                      {walletRows.length || editableFees.length || 0}
                    </p>
                    <p className="mt-1 text-sm leading-snug text-slate-500">Payout rows in the current finance review view.</p>
                  </div>
                </div>
              </Col>
              <Col md={6} lg={3}>
                <div className="rounded-[20px] overflow-hidden h-full">
                  <div className="border-l bg-white py-2 px-[22px] h-full shadow-md" style={{ borderLeft: '6px solid #2297D1' }}>
                    <p className="text-[11px] xl:text-base uppercase  ">Needs Review</p>
                    <p className="mt-2 text-2xl font-bold tracking-tight text-black sm:text-3xl">0</p>
                    <p className="mt-1 text-sm leading-snug text-slate-500">Rows where received and distributed values still need a closer look.</p>
                  </div>
                </div>
              </Col>
              <Col md={6} lg={3}>
                <div className="rounded-[20px] overflow-hidden h-full">
                  <div className="border-l bg-white py-2 px-[22px] h-full shadow-md" style={{ borderLeft: '6px solid #2297D1' }}>
                    <p className="text-[11px] xl:text-base uppercase ">Missing Data</p>
                    <p className="mt-2 text-2xl font-bold tracking-tight text-black sm:text-3xl">0</p>
                    <p className="mt-1 text-sm leading-snug text-slate-500">Rows still waiting for one or more on-chain values to come through.</p>
                  </div>
                </div>
              </Col>
              <Col md={6} lg={3}>
                <div className="rounded-[20px] overflow-hidden h-full">
                  <div className="border-l bg-white py-2 px-[22px] h-full shadow-md" style={{ borderLeft: '6px solid #2297D1' }}>
                    <p className="text-[11px] xl:text-base uppercase  ">Variance Watch</p>
                    <p className="mt-2 text-2xl font-bold tracking-tight text-black sm:text-3xl">0</p>
                    <p className="mt-1 text-sm leading-snug text-slate-500">Rows with a visible variance that deserves a second check.</p>
                  </div>
                </div>
              </Col>
            </Row>
            
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            {/* Tabs for Admin, SuperAgent, and Agent roles */}
            {shouldShowTabs ? (
              <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
                <Nav
                  variant="tabs"
                  className="mb-4 flex-nowrap gap-1 overflow-x-auto rounded-2xl border border-[#66666685] bg-[#9296982B] p-2 [&_.nav-item]:shrink-0 [&_.nav-link]:rounded-xl [&_.nav-link]:border-0 [&_.nav-link]:px-6 [&_.nav-link]:py-2.5 [&_.nav-link]:text-sm [&_.nav-link]:font-medium [&_.nav-link]:text-slate-700 [&_.nav-link]:bg-transparent [&_.nav-link:hover]:text-slate-900 font-semibold"
                >
                  {/* Fee customize tab for Admin and SuperAgent, not for Agent */}
                  {(userRole === 'Admin' || userRole === 'SuperAgent') && (
                    <Nav.Item>
                      <Nav.Link 
                        eventKey="fee-customise" 
                        className={activeTab === 'fee-customise' ? '!border !border-[#67b9e0] !bg-[#2297D121] !text-slate-900 shadow-sm' : ''}
                      >
                        Fee customize
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  <Nav.Item>
                    <Nav.Link
                      eventKey="wallets"
                      className={activeTab === 'wallets' ? '!border !border-[#67b9e0] !bg-[#2297D121] !text-slate-900 shadow-sm' : ''}
                    >
                      Wallets
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      eventKey="telegram-ids" 
                      className={activeTab === 'telegram-ids' ? '!border !border-[#67b9e0] !bg-[#2297D121] !text-slate-900 shadow-sm' : ''}
                    >
                      Telegram IDs
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="api-access"
                      className={activeTab === 'api-access' ? '!border !border-[#67b9e0] !bg-[#2297D121] !text-slate-900 shadow-sm' : ''}
                    >
                      API Access
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="iframe"
                      className={activeTab === 'iframe' ? '!border !border-[#67b9e0] !bg-[#2297D121] !text-slate-900 shadow-sm' : ''}
                    >
                      Iframe
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  {(userRole === 'Admin' || userRole === 'SuperAgent') && (
                    <Tab.Pane eventKey="fee-customise">
                      {renderFeeCustomiseTab()}
                    </Tab.Pane>
                  )}
                  <Tab.Pane eventKey="wallets">
                    {renderWalletsTab()}
                  </Tab.Pane>
                  <Tab.Pane eventKey="telegram-ids">
                    {renderTelegramIdsTab()}
                  </Tab.Pane>
                  <Tab.Pane eventKey="api-access">
                    {renderApiAccessTab()}
                  </Tab.Pane>
                  <Tab.Pane eventKey="iframe">
                    {renderIframeTab()}
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            ) : null}

            {/* Add Custom Fee Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered contentClassName="!rounded-[28px] blurLayerInteractive-modal !border-0">
              <Modal.Header closeButton className="px-4 pt-4 pb-3 !border-b !border-[#D8D8D8]">
                <Modal.Title className="font-semibold text-2xl  text-[#202020]">Add Custom Fee</Modal.Title>
              </Modal.Header>
              <Modal.Body className=" px-4 pt-3 pb-2">
                <Form className="pt-1">
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold text-[#313131] mb-2">Payment paths</Form.Label>
                    <Form.Control
                      type="text"
                      value="Direct Crypto & Fiat Onramp (shared)"
                      readOnly
                      className="!bg-[#F4F4F4] !border-[#D1D1D1] rounded-4 py-2 px-3 text-[#4A4A4A]"
                    />
                    <Form.Text className="text-[#5F5F5F]">
                      This custom fee applies to both settlement paths for the selected user.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold text-[#313131] mb-2">Fee Type</Form.Label>
                    <Form.Control
                      type="text"
                      value={newFee.feeType}
                      readOnly
                      className="!bg-[#F4F4F4] !border-[#D1D1D1] rounded-4 py-2 px-3 text-[#4A4A4A]"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold text-[#313131] mb-2">User</Form.Label>
                    <Form.Select
                      className="!bg-[#F4F4F4] !border-[#D1D1D1] rounded-4 py-2 px-3 text-[#4A4A4A]"
                      value={newFee.userId}
                      onChange={(e) => setNewFee({ ...newFee, userId: parseInt(e.target.value, 10) })}
                    >
                      <option value={0}>
                        {getFeeEligibleUsers(newFee.feeType).length === 0
                          ? `No ${newFee.feeType} user available`
                          : `Select ${newFee.feeType} user`}
                      </option>
                      {getFeeEligibleUsers(newFee.feeType).map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label className="fw-semibold text-[#313131] mb-2">Percentage</Form.Label>
                    <Form.Control
                      type="number"
                      className="!bg-[#F4F4F4] !border-[#D1D1D1] rounded-4 py-2 px-3 text-[#4A4A4A]"
                      value={newFee.percent || ''}
                      onChange={(e) => setNewFee({ ...newFee, percent: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Enter percentage"
                    />
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer className="px-4 pb-4 pt-2 !border-0">
                <Button
                  variant="outline-secondary"
                  className="!border-[#CFCFCF] !text-[#3A3A3A] !bg-white hover:!bg-[#F4F4F4] rounded-4 px-4 py-2 fw-medium"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="rounded-4 px-4 py-2 fw-semibold"
                  onClick={handleAddCustomFee}
                  disabled={getFeeEligibleUsers(newFee.feeType).length === 0}
                >
                  Add Fee
                </Button>
              </Modal.Footer>
            </Modal>

            <Modal
              show={showWalletModal}
              onHide={handleCloseWalletModal}
              centered
              backdrop="static"
              enforceFocus={false}
              contentClassName="!rounded-[28px] !border-0 overflow-hidden"
            >
              <Modal.Header closeButton className="px-4 pt-4 pb-3 !border-b !border-[#D8D8D8] !bg-[#1e1e1e]">
                <Modal.Title className="font-semibold text-2xl text-white">
                  Edit Wallet Address
                  {selectedWalletUser && <span className="text-slate-400 font-normal text-lg ml-2">— {selectedWalletUser.name}</span>}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="px-4 pt-4 pb-2 bg-[#f6f6f6]">
                {walletModalError && (
                  <Alert
                    variant="danger"
                    className="mb-3 py-2 small"
                    dismissible
                    onClose={() => setWalletModalError(null)}
                  >
                    {walletModalError}
                  </Alert>
                )}
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleUpdateWallet();
                  }}
                >
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold text-[#313131] mb-2 text-sm uppercase tracking-[0.1em]">
                      {selectedWalletUser?.role === 'Admin'
                        ? 'Company commission wallet (Com) — USDC Polygon'
                        : 'Merchant payout wallet (Dest) — USDC Polygon'}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      autoFocus
                      value={walletAddress}
                      onChange={(e) => {
                        setWalletAddress(e.target.value);
                        if (walletModalError) setWalletModalError(null);
                      }}
                      placeholder="Enter ERC-20 wallet address (0x...)"
                      autoComplete="off"
                      className="!bg-white !border-[#d1d9e0] p-[12px] rounded-xl"
                    />
                    <Form.Text className="text-[#5F5F5F] text-sm">
                      {selectedWalletUser?.role === 'Admin'
                        ? 'Fixed platform Com wallet for all merchants. Must be set before creating payment links.'
                        : 'Destination wallet for the merchant share after the split (USDC on Polygon, 0x…).'}
                    </Form.Text>
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer className="px-4 pb-4 pt-3 !border-0 bg-[#f6f6f6]">
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="!border-[#CFCFCF] !text-[#3A3A3A] !bg-white hover:!bg-[#F4F4F4] rounded-[6px] px-[13px] py-2 text-sm fw-medium"
                  onClick={handleCloseWalletModal}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  className="!bg-[#2297D1] !border-[#2297D1] rounded-[6px] px-[13px] py-2 text-sm fw-semibold hover:!bg-[#1b7fb3]"
                  onClick={() => void handleUpdateWallet()}
                  disabled={walletLoading}
                >
                  {walletLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    'Update Wallet'
                  )}
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Add Telegram Account Modal */}
            <Modal show={showTelegramModal} onHide={() => setShowTelegramModal(false)} centered contentClassName="!rounded-[28px] blurLayerInteractive-modal !border-0">
              <Modal.Header closeButton className="px-4 pt-4 pb-3 !border-b !border-[#D8D8D8]">
                <Modal.Title className="font-semibold text-2xl  text-[#202020]">Add Telegram Account</Modal.Title>
              </Modal.Header>
              <Modal.Body className=" px-4 pt-3 pb-2">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold text-[#313131] mb-2 text-base ">Telegram ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={newTelegramId}
                      onChange={(e) => setNewTelegramId(e.target.value)}
                      placeholder="Enter Telegram ID (numeric)"
                    />
                    <Form.Text className="text-[#5F5F5F] text-sm">
                      Enter the numeric Telegram ID. Use the "Get Telegram ID" button to find your ID from a username.
                    </Form.Text>
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer className="px-4 pb-4 pt-2 !border-0">
                <Button
                  variant="outline-secondary"
                  className="!border-[#CFCFCF] !text-[#3A3A3A] !bg-white hover:!bg-[#F4F4F4] rounded-4 px-4 py-2 fw-medium"
                  onClick={() => setShowTelegramModal(false)}
                >
                  Cancel
                </Button>
                <Button variant="primary" className="rounded-4 px-4 py-2 fw-semibold" onClick={handleAddTelegramAccount}>
                  Add Account
                </Button>
              </Modal.Footer>
            </Modal>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Settings;